/**
 * ContentListCenter - Shared list/board view for content items
 *
 * Used by both ContentReaderPage (center panel when no item selected)
 * and ContentListPage (full-page content board).
 */

import { useState } from 'react';
import {
  Search,
  List,
  LayoutGrid,
  FileText,
  ChevronRight,
  Plus,
  Megaphone,
  Loader2,
} from 'lucide-react';
import {
  STATUS_LABELS,
  STATUS_COLORS,
  CONTENT_TYPE_LABELS,
  ALL_STATUSES,
  type ContentItem,
  type ContentStatus,
} from '../../../api/content';
import { ContentCard } from '../../../components/shared/ContentCard';

// =============================================================================
// Props
// =============================================================================

export interface ContentListCenterProps {
  items: ContentItem[];
  isLoading: boolean;
  onItemClick: (id: string) => void;
  onCreateItem: () => void;
  /** Override the default header title */
  title?: string;
}

// =============================================================================
// ContentListCenter — toolbar + list/board view
// =============================================================================

export function ContentListCenter({
  items,
  isLoading,
  onItemClick,
  onCreateItem,
  title = 'Content Library',
}: ContentListCenterProps) {
  const [viewMode, setViewMode] = useState<'list' | 'board'>('list');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ContentStatus | ''>('');

  const filtered = items.filter((item) => {
    if (search) {
      const q = search.toLowerCase();
      if (!item.title.toLowerCase().includes(q) && !item.campaign?.toLowerCase().includes(q)) {
        return false;
      }
    }
    if (statusFilter && item.status !== statusFilter) return false;
    return true;
  });

  return (
    <div className="flex-1 flex flex-col min-w-0">
      {/* Header */}
      <div className="h-12 flex items-center justify-between px-6 border-b border-neutral-100 bg-neutral-50/50 shrink-0">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-neutral-800">{title}</h2>
          <span className="text-xs text-neutral-400">{items.length} item{items.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 px-6 py-3 shrink-0">
        {/* View tabs */}
        <div className="flex items-center bg-neutral-100 rounded-lg p-0.5">
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors
              ${viewMode === 'list' ? 'bg-white text-neutral-800 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'}`}
          >
            <List size={14} />
            List
          </button>
          <button
            onClick={() => setViewMode('board')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors
              ${viewMode === 'board' ? 'bg-white text-neutral-800 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'}`}
          >
            <LayoutGrid size={14} />
            Board
          </button>
        </div>

        {/* Search */}
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search content..."
            className="w-full pl-8 pr-3 py-2 text-sm bg-white border border-neutral-200 rounded-lg
              focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-300"
          />
        </div>

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ContentStatus | '')}
          className="px-3 py-2 text-sm bg-white border border-neutral-200 rounded-lg
            focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-300"
        >
          <option value="">All Statuses</option>
          {ALL_STATUSES.map((s) => (
            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
          ))}
        </select>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-auto px-6 pb-6">
        {viewMode === 'board' ? (
          <BoardView items={filtered} onItemClick={onItemClick} />
        ) : (
          <ListView items={filtered} loading={isLoading} onItemClick={onItemClick} onCreateFirst={onCreateItem} />
        )}
      </div>
    </div>
  );
}

// =============================================================================
// List View
// =============================================================================

export function ListView({
  items,
  loading,
  onItemClick,
  onCreateFirst,
  selectedIds,
  onToggleSelect,
  onToggleAll,
}: {
  items: ContentItem[];
  loading: boolean;
  onItemClick: (id: string) => void;
  onCreateFirst: () => void;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onToggleAll?: () => void;
}) {
  const selectable = !!selectedIds && !!onToggleSelect;
  const allSelected = selectable && items.length > 0 && items.every(i => selectedIds.has(i.id));
  const someSelected = selectable && items.some(i => selectedIds.has(i.id)) && !allSelected;

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-3.5 border-b border-neutral-100 animate-pulse">
            <div className="w-8 h-8 rounded-lg bg-neutral-100" />
            <div className="h-4 w-48 bg-neutral-200 rounded" />
            <div className="h-4 w-20 bg-neutral-100 rounded" />
            <div className="h-4 w-24 bg-neutral-100 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-16 text-center max-w-lg w-full">
          <div className="w-16 h-16 bg-coral-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Megaphone className="w-8 h-8 text-coral-400" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">No content items found</h3>
          <p className="text-sm text-neutral-500 mb-6 leading-relaxed">
            Create your first piece of content or import from Notion to get started with your content pipeline.
          </p>
          <button
            onClick={onCreateFirst}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-coral-500 text-white rounded-xl
              font-medium text-sm hover:bg-coral-600 transition-colors shadow-sm"
          >
            <Plus size={16} />
            Create First Content
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
      {/* Header */}
      <div className={`grid gap-2 px-4 py-2.5 bg-neutral-50 border-b border-neutral-200 text-xs font-semibold text-neutral-500 uppercase tracking-wider ${selectable ? 'grid-cols-[28px_1fr_1fr_2fr_1fr_2fr_2fr]' : 'grid-cols-12'}`}>
        {selectable && (
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={allSelected}
              ref={el => { if (el) el.indeterminate = someSelected; }}
              onChange={onToggleAll}
              className="w-3.5 h-3.5 rounded border-neutral-300 text-coral-500 focus:ring-coral-500/20 cursor-pointer"
            />
          </div>
        )}
        <div className={selectable ? '' : 'col-span-4'}>Title</div>
        <div className={selectable ? '' : 'col-span-1'}>Type</div>
        <div className={selectable ? '' : 'col-span-2'}>Campaign</div>
        <div className={selectable ? '' : 'col-span-1'}>Status</div>
        <div className={selectable ? '' : 'col-span-2'}>Author</div>
        <div className={selectable ? '' : 'col-span-2'}>Publish Date</div>
      </div>

      {/* Rows */}
      {items.map((item) => {
        const isSelected = selectable && selectedIds.has(item.id);
        return (
          <div
            key={item.id}
            className={`grid gap-2 px-4 py-3 border-b border-neutral-100
              hover:bg-neutral-50 transition-colors w-full text-left group
              ${isSelected ? 'bg-coral-50/50' : ''}
              ${selectable ? 'grid-cols-[28px_1fr_1fr_2fr_1fr_2fr_2fr]' : 'grid-cols-12'}`}
          >
            {selectable && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => { e.stopPropagation(); onToggleSelect(item.id); }}
                  className="w-3.5 h-3.5 rounded border-neutral-300 text-coral-500 focus:ring-coral-500/20 cursor-pointer"
                />
              </div>
            )}
            <button
              onClick={() => onItemClick(item.id)}
              className={`flex items-center gap-2 min-w-0 ${selectable ? '' : 'col-span-4'}`}
            >
              <FileText size={14} className="text-neutral-400 shrink-0" />
              <span className="text-sm font-medium text-neutral-800 truncate group-hover:text-coral-600 transition-colors">
                {item.title}
              </span>
              {item.is_featured && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                  Featured
                </span>
              )}
            </button>
            <div className={`text-xs text-neutral-500 flex items-center ${selectable ? '' : 'col-span-1'}`}>
              {CONTENT_TYPE_LABELS[item.content_type]}
            </div>
            <div className={`text-xs text-neutral-500 flex items-center truncate ${selectable ? '' : 'col-span-2'}`}>
              {item.campaign || '\u2014'}
            </div>
            <div className={`flex items-center ${selectable ? '' : 'col-span-1'}`}>
              <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[item.status]}`}>
                {STATUS_LABELS[item.status]}
              </span>
            </div>
            <div className={`text-xs text-neutral-500 flex items-center truncate ${selectable ? '' : 'col-span-2'}`}>
              {item.author_name || '\u2014'}
            </div>
            <div className={`text-xs text-neutral-500 flex items-center justify-between ${selectable ? '' : 'col-span-2'}`}>
              <span>
                {item.planned_publish_date
                  ? new Date(item.planned_publish_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : '\u2014'}
              </span>
              <ChevronRight size={14} className="text-neutral-300 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// Board View (Kanban by Status)
// =============================================================================

export function BoardView({
  items,
  onItemClick,
}: {
  items: ContentItem[];
  onItemClick: (id: string) => void;
}) {
  const columns: ContentStatus[] = ['planned', 'drafting', 'design_ready', 'in_review', 'final_edits', 'ready_to_publish', 'published'];

  return (
    <div className="flex gap-3 overflow-x-auto pb-4 kanban-scrollbar-subtle">
      {columns.map((status) => {
        const colItems = items.filter((i) => i.status === status);
        return (
          <div key={status} className="flex-shrink-0 w-64 bg-neutral-50 rounded-xl border border-neutral-200">
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-neutral-200">
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${STATUS_COLORS[status].split(' ')[0]}`} />
                <span className="text-xs font-semibold text-neutral-600">{STATUS_LABELS[status]}</span>
              </div>
              <span className="text-xs text-neutral-400 tabular-nums">{colItems.length}</span>
            </div>
            <div className="p-2 space-y-2 min-h-[120px] max-h-[60vh] overflow-auto kanban-scrollbar-subtle">
              {colItems.map((item) => (
                <ContentCard
                  key={item.id}
                  item={item}
                  onClick={() => onItemClick(item.id)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
